import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Divider,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  AccountBalance as BillingIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  AttachMoney as CostIcon,
  Speed as UsageIcon,
  Psychology as AIIcon,
  Transcribe as TranscribeIcon,
  DateRange as DateIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const Billing = () => {
  const { user } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('current_month');
  const [filters, setFilters] = useState({
    provider: '',
    service: '',
    startDate: '',
    endDate: ''
  });

  // Data states
  const [summary, setSummary] = useState({
    totalCost: 0,
    summary: [],
    period: {}
  });
  const [details, setDetails] = useState({
    details: [],
    pagination: { total: 0, page: 1, limit: 50, pages: 0 }
  });
  const [breakdown, setBreakdown] = useState({
    dailyUsage: [],
    period: {}
  });
  const [pricing, setPricing] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    fetchData();
  }, [dateRange, currentTab, page, rowsPerPage, filters]);

  useEffect(() => {
    fetchPricingInfo();
  }, []);

  const getDateRange = () => {
    const endDate = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case 'current_month':
        startDate = startOfMonth(endDate);
        break;
      case 'last_month':
        const lastMonth = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1);
        startDate = startOfMonth(lastMonth);
        endDate.setTime(endOfMonth(lastMonth).getTime());
        break;
      case 'last_7_days':
        startDate = subDays(endDate, 7);
        break;
      case 'last_30_days':
        startDate = subDays(endDate, 30);
        break;
      case 'last_90_days':
        startDate = subDays(endDate, 90);
        break;
      case 'custom':
        if (filters.startDate && filters.endDate) {
          startDate = new Date(filters.startDate);
          endDate.setTime(new Date(filters.endDate).getTime());
        }
        break;
      default:
        startDate = startOfMonth(endDate);
    }

    return { startDate, endDate };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();
      const token = localStorage.getItem('token');

      const baseParams = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        ...(filters.provider && { provider: filters.provider }),
        ...(filters.service && { service: filters.service })
      };

      if (currentTab === 0) {
        // Fetch summary
        const response = await axios.get(`${API_URL}/billing/summary`, {
          params: baseParams,
          headers: { Authorization: `Bearer ${token}` }
        });
        setSummary(response.data.data);
      } else if (currentTab === 1) {
        // Fetch details
        const response = await axios.get(`${API_URL}/billing/details`, {
          params: {
            ...baseParams,
            page: page + 1,
            limit: rowsPerPage
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        setDetails(response.data.data);
      } else if (currentTab === 2) {
        // Fetch usage breakdown
        const response = await axios.get(`${API_URL}/billing/breakdown`, {
          params: baseParams,
          headers: { Authorization: `Bearer ${token}` }
        });
        setBreakdown(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError('Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/billing/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPricing(response.data.data);
    } catch (err) {
      console.error('Error fetching pricing info:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0); // Reset pagination when changing tabs
  };

  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const exportData = () => {
    // Implementation for exporting billing data
    console.log('Export functionality would be implemented here');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getProviderIcon = (provider) => {
    switch (provider?.toLowerCase()) {
      case 'openai':
        return <AIIcon />;
      case 'assembly':
      case 'assemblyai':
        return <TranscribeIcon />;
      default:
        return <AIIcon />;
    }
  };

  const getServiceColor = (service) => {
    switch (service?.toLowerCase()) {
      case 'transcription':
        return 'info';
      case 'analysis':
        return 'success';
      default:
        return 'default';
    }
  };

  const renderSummaryTab = () => (
    <Grid container spacing={3}>
      {/* Cost Overview Cards */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Cost
                </Typography>
                <Typography variant="h4" component="div">
                  {formatCurrency(summary.totalCost)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {summary.period.startDate && format(new Date(summary.period.startDate), 'MMM dd')} - 
                  {summary.period.endDate && format(new Date(summary.period.endDate), 'MMM dd')}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <CostIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Transcription Calls
                </Typography>
                <Typography variant="h4" component="div">
                  {summary.summary?.filter(s => s.service === 'transcription').reduce((acc, s) => acc + parseInt(s.count), 0) || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  AI transcribed calls
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'info.main' }}>
                <TranscribeIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Analysis Calls
                </Typography>
                <Typography variant="h4" component="div">
                  {summary.summary?.filter(s => s.service === 'analysis').reduce((acc, s) => acc + parseInt(s.count), 0) || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  AI analyzed calls
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'success.main' }}>
                <AIIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Avg Cost/Call
                </Typography>
                <Typography variant="h4" component="div">
                  {formatCurrency(
                    summary.summary?.length > 0 
                      ? summary.totalCost / summary.summary?.reduce((acc, s) => acc + parseInt(s.count), 0)
                      : 0
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Average per call
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'warning.main' }}>
                <UsageIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Provider & Service Breakdown */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Cost by Provider" />
          <CardContent>
            {summary.summary?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={summary.summary?.reduce((acc, item) => {
                      const existing = acc.find(a => a.providerName === item.providerName);
                      if (existing) {
                        existing.value += parseFloat(item.totalCost);
                      } else {
                        acc.push({
                          name: item.providerName || item.provider,
                          value: parseFloat(item.totalCost)
                        });
                      }
                      return acc;
                    }, [])}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {summary.summary?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="textSecondary" align="center">No data available</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Cost by Service" />
          <CardContent>
            {summary.summary?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summary.summary?.reduce((acc, item) => {
                  const existing = acc.find(a => a.name === item.service);
                  if (existing) {
                    existing.cost += parseFloat(item.totalCost);
                    existing.calls += parseInt(item.count);
                  } else {
                    acc.push({
                      name: item.service,
                      cost: parseFloat(item.totalCost),
                      calls: parseInt(item.count)
                    });
                  }
                  return acc;
                }, [])}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip formatter={(value, name) => 
                    name === 'cost' ? formatCurrency(value) : value
                  } />
                  <Legend />
                  <Bar dataKey="cost" fill="#8884d8" name="Cost" />
                  <Bar dataKey="calls" fill="#82ca9d" name="Calls" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="textSecondary" align="center">No data available</Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Detailed Summary Table */}
      <Grid item xs={12}>
        <Card>
          <CardHeader title="Usage Summary by Provider & Service" />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="right">Total Units</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.summary?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getProviderIcon(row.provider)}
                          {row.providerName || row.provider}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.service} 
                          color={getServiceColor(row.service)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{row.model}</TableCell>
                      <TableCell align="right">{parseInt(row.count).toLocaleString()}</TableCell>
                      <TableCell align="right">{parseInt(row.totalUnits || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{formatCurrency(row.totalCost)}</TableCell>
                    </TableRow>
                  )) || (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="textSecondary">No usage data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderDetailsTab = () => (
    <Card>
      <CardHeader 
        title="Detailed Usage Records" 
        action={
          <Button
            startIcon={<DownloadIcon />}
            onClick={exportData}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
        }
      />
      <CardContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.details?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {format(new Date(row.usage_date), 'MMM dd, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getProviderIcon(row.provider)}
                      {row.providerName || row.provider}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={row.service} 
                      color={getServiceColor(row.service)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{row.model}</TableCell>
                  <TableCell align="right">
                    {row.duration_seconds ? `${Math.round(row.duration_seconds / 60)}m` : '-'}
                  </TableCell>
                  <TableCell align="right">{row.units?.toLocaleString() || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(row.cost)}</TableCell>
                </TableRow>
              )) || (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">No detailed records available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={details.pagination?.total || 0}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </CardContent>
    </Card>
  );

  const renderBreakdownTab = () => (
    <Card>
      <CardHeader title="Usage Trends" />
      <CardContent>
        {breakdown.dailyUsage?.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={breakdown.dailyUsage}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <RechartsTooltip 
                formatter={(value, name) => 
                  name === 'totalCost' ? formatCurrency(value) : value
                }
              />
              <Legend />
              <Line type="monotone" dataKey="totalCost" stroke="#8884d8" name="Daily Cost" />
              <Line type="monotone" dataKey="count" stroke="#82ca9d" name="Daily Calls" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography color="textSecondary" align="center">No trend data available</Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading && currentTab === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>💰 AI Usage & Billing</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          💰 AI Usage & Billing
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={handleDateRangeChange}
            >
              <MenuItem value="current_month">Current Month</MenuItem>
              <MenuItem value="last_month">Last Month</MenuItem>
              <MenuItem value="last_7_days">Last 7 Days</MenuItem>
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="last_90_days">Last 90 Days</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Custom Date Range */}
      {dateRange === 'custom' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  value={filters.provider}
                  label="Provider"
                  onChange={(e) => handleFilterChange('provider', e.target.value)}
                >
                  <MenuItem value="">All Providers</MenuItem>
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="assembly">AssemblyAI</MenuItem>
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="azure">Azure</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Service</InputLabel>
                <Select
                  value={filters.service}
                  label="Service"
                  onChange={(e) => handleFilterChange('service', e.target.value)}
                >
                  <MenuItem value="">All Services</MenuItem>
                  <MenuItem value="transcription">Transcription</MenuItem>
                  <MenuItem value="analysis">Analysis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Summary" />
          <Tab label="Details" />
          <Tab label="Trends" />
        </Tabs>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tab Content */}
      {!loading && (
        <>
          {currentTab === 0 && renderSummaryTab()}
          {currentTab === 1 && renderDetailsTab()}
          {currentTab === 2 && renderBreakdownTab()}
        </>
      )}
    </Box>
  );
};

export default Billing; 